const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

export type CallerRow = {
  phone: string;
  name: string | null;
  call_count: number;
  last_call_at: string;
  open_items_count: number;
};

export async function fetchCallers(
  customerId: string | number,
  limit = 100,
): Promise<{ callers: CallerRow[]; total: number }> {
  const res = await fetch(`${API_BASE}/customers/${customerId}/callers?limit=${limit}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const rawCallers: any[] = Array.isArray(data?.callers) ? data.callers : [];
  const callers: CallerRow[] = rawCallers.map((c) => ({
    phone: String(c?.phone ?? ""),
    name: typeof c?.name === "string" && c.name.trim() ? c.name.trim() : null,
    call_count: Number(c?.call_count ?? 0),
    last_call_at: String(c?.last_call_at ?? ""),
    open_items_count: Number(c?.open_items_count ?? 0),
  }));
  const total = typeof data?.total === "number" ? data.total : callers.length;
  return { callers, total };
}

export async function fetchCallerName(
  customerId: string | number,
  phone10: string,
): Promise<string | null> {
  try {
    const res = await fetch(
      `${API_BASE}/customers/${customerId}/user-memories?phone=${encodeURIComponent(phone10)}`,
      { credentials: "include" },
    );
    if (!res.ok) return null;
    const data = await res.json();
    const name = data?.identity?.preferred_name;
    return typeof name === "string" && name.trim() ? name.trim() : null;
  } catch {
    return null;
  }
}

export async function hydrateNames(
  customerId: string | number,
  rows: CallerRow[],
  max = 10,
): Promise<Map<string, string>> {
  const targets = rows.filter((r) => r.name === null).slice(0, max);
  const names = new Map<string, string>();
  let next = 0;
  const worker = async () => {
    while (next < targets.length) {
      const row = targets[next++];
      const name = await fetchCallerName(customerId, row.phone);
      if (name) names.set(row.phone, name);
    }
  };
  await Promise.all(Array.from({ length: Math.min(3, targets.length) }, worker));
  return names;
}
